import { useState, useEffect, useRef } from 'react';
import './App.css';

// WhatsApp Floating Button
const WhatsAppButton = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isPulsing, setIsPulsing] = useState(true);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 2000);
    const pulseTimer = setTimeout(() => setIsPulsing(false), 10000);
    return () => {
      clearTimeout(timer);
      clearTimeout(pulseTimer);
    };
  }, []);

  const handleClick = () => {
    window.open('https://wa.me/590691283484?text=Hello! I\'m interested in your concierge services.', '_blank');
  };

  return (
    <button
      className={`whatsapp-btn ${isVisible ? 'visible' : ''} ${isPulsing ? 'pulsing' : ''}`}
      onClick={handleClick}
      aria-label="Contact us on WhatsApp"
    >
      <svg viewBox="0 0 24 24" fill="currentColor">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      <span className="whatsapp-tooltip">Chat with us</span>
    </button>
  );
};

// Scroll Progress Indicator
const ScrollProgress = () => {
  const [progress, setProgress] = useState(0);

  useEffect(() => {
    const handleScroll = () => {
      const totalHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrollPosition = window.scrollY;
      setProgress((scrollPosition / totalHeight) * 100);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return <div className="scroll-progress" style={{ width: `${progress}%` }} />;
};

// Animated Section Hook
const useScrollAnimation = (threshold = 0.2) => {
  const [isVisible, setIsVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isVisible];
};

// Navigation Component
const Navigation = () => {
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <nav className={`navbar ${scrolled ? 'scrolled' : ''}`}>
      <a href="/" className="nav-logo">
        <img src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/st-barth-inside-logo.png" alt="St Barth Insider" />
      </a>
      <div className="nav-links">
        <a href="#about">experience</a>
        <a href="#services">services</a>
        <a href="#villas">villas</a>
        <a href="#yachts">yachts</a>
        <a href="#regatta">regatta 2026</a>
      </div>
      <button className="nav-cta" onClick={() => document.getElementById('contact')?.scrollIntoView({ behavior: 'smooth' })}><span>Reserve</span></button>
    </nav>
  );
};

// Hero Section - Full bleed yacht image
const Hero = () => {
  const [loaded, setLoaded] = useState(false);

  // Check if it's night time (between 7pm and 6am)
  const hour = new Date().getHours();
  const isNight = hour >= 19 || hour < 6;

  const heroImage = isNight
    ? "https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/saint-barth-night.jpg"
    : "https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/stbarthinsider/gustaf-airport.png";

  useEffect(() => {
    setLoaded(true);
  }, []);

  const scrollToAbout = () => {
    document.getElementById('about')?.scrollIntoView({ behavior: 'smooth' });
  };

  return (
    <section className="hero-section">
      <div className="hero-image">
        <img
          src={heroImage}
          alt="St Barth luxury view"
        />
      </div>
      <div className={`hero-title ${loaded ? 'loaded' : ''}`}>
        <h1>
          <span className="hero-line hero-line-1">St Barth.</span>
          <span className="hero-line hero-line-2">Unlocked.</span>
        </h1>
      </div>
      <div className={`hero-content ${loaded ? 'loaded' : ''}`}>
        <p className="hero-tagline">{"// your personal concierge to st barth's most exclusive experiences"}</p>
        <button className="hero-cta" onClick={scrollToAbout}>
          get your custom proposal
          <span className="cta-arrow">→</span>
        </button>
      </div>
      <div className="scroll-indicator" onClick={scrollToAbout}>
        <div className="scroll-mouse">
          <div className="scroll-wheel"></div>
        </div>
        <span>Scroll</span>
      </div>
    </section>
  );
};

// About Section
const AboutSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);

  return (
    <section className="about-section" id="about" ref={ref}>
      <div className="about-image">
        <img
          src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/st-barth-view-online.jpg"
          alt="St Barth beach"
        />
        <div className="about-image-accent"></div>
      </div>
      <div className={`about-content ${isVisible ? 'animate-in' : ''}`}>
        <div className="about-header">
          <span className="about-eyebrow">Gustavia, St Barthélemy</span>
          <h2>The Art of <em>Island Living</em></h2>
        </div>
        <div className="about-intro">
          <p>
            Born from a passion for Saint Barthélemy's unique blend of Caribbean warmth
            and French sophistication, we craft experiences that capture the true essence
            of island living.
          </p>
        </div>
        <div className="about-philosophy">
          <div className="philosophy-item">
            <span className="philosophy-number">01</span>
            <div className="philosophy-content">
              <h4>Effortless Luxury</h4>
              <p>The best experiences feel natural. We handle every detail so you can simply enjoy.</p>
            </div>
          </div>
          <div className="philosophy-item">
            <span className="philosophy-number">02</span>
            <div className="philosophy-content">
              <h4>Island Connections</h4>
              <p>Years of relationships mean access to tables, villas, and yachts others can't reach.</p>
            </div>
          </div>
          <div className="philosophy-item">
            <span className="philosophy-number">03</span>
            <div className="philosophy-content">
              <h4>Your Rhythm</h4>
              <p>Whether sunrise yoga or sunset champagne, we match the island to your pace.</p>
            </div>
          </div>
        </div>
        <div className="about-cta">
          <a href="#contact" className="about-btn">begin your bespoke experience</a>
        </div>
      </div>
    </section>
  );
};

// Experiences Section - Curated Packages
const ExperiencesSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);

  const experiences = [
    {
      title: "Sunset Yachting",
      description: "Private catamaran cruise with champagne and canapés as the sun sets over Gustavia harbor",
      duration: "2 hours"
    },
    {
      title: "Colombier Beach Walk",
      description: "Hike to St Barth's most secluded beach — crystal-clear waters, no roads, no crowds",
      duration: "Half day"
    },
    {
      title: "Culinary Journey",
      description: "Personal chef prepares a 5-course meal at your villa using local Caribbean ingredients",
      duration: "Evening"
    },
    {
      title: "Wellness Retreat",
      description: "Beachside yoga, spa treatments, and organic cuisine for complete rejuvenation",
      duration: "Full day"
    }
  ];

  return (
    <section className="experiences-section" ref={ref}>
      <div className={`experiences-content ${isVisible ? 'animate-in' : ''}`}>
        <div className="experiences-header">
          <span className="section-label">Signature Experiences</span>
          <h2>Curated <em>Moments</em></h2>
          <p>Handcrafted experiences designed exclusively for our guests</p>
        </div>
        <div className="experiences-grid">
          {experiences.map((exp, index) => (
            <div key={index} className="experience-card">
              <div className="experience-number">{String(index + 1).padStart(2, '0')}</div>
              <h3>{exp.title}</h3>
              <p>{exp.description}</p>
              <div className="experience-details">
                <span className="experience-duration">{exp.duration}</span>
              </div>
              <a href="#contact" className="experience-cta">Inquire</a>
            </div>
          ))}
        </div>
        <div className="experiences-footer">
          <p>All experiences are fully customizable. <a href="#contact">Tell us your dream</a> and we'll create it.</p>
        </div>
      </div>
    </section>
  );
};

// Trust Section - Why Choose Us
const TrustSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);

  return (
    <section className="trust-section" ref={ref}>
      <div className={`trust-content ${isVisible ? 'animate-in' : ''}`}>
        <div className="trust-header">
          <span className="section-label">Why Us</span>
          <h2>The <em>Insider</em> Difference</h2>
        </div>
        <div className="trust-grid">
          <div className="trust-item">
            <div className="trust-icon">✦</div>
            <h4>24/7 Concierge</h4>
            <p>Your dedicated concierge is always a message away, day or night, throughout your stay.</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">✦</div>
            <h4>Local Expertise</h4>
            <p>Years on the island means we know every hidden gem, every chef, every captain by name.</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">✦</div>
            <h4>Exclusive Access</h4>
            <p>Reservations others can't get, villas that aren't listed, experiences that don't exist — until you ask.</p>
          </div>
          <div className="trust-item">
            <div className="trust-icon">✦</div>
            <h4>Tailored for You</h4>
            <p>No cookie-cutter itineraries. Every recommendation is crafted for your preferences and style.</p>
          </div>
        </div>
        <div className="trust-guarantees">
          <span className="guarantee-item">48h booking confirmation</span>
          <span className="guarantee-item">no hidden fees</span>
          <span className="guarantee-item">24/7 on-island support</span>
        </div>
        <div className="trust-cta">
          <p>Ready to experience the difference?</p>
          <a href="https://wa.me/590691283484" className="trust-btn">
            start planning
            <span>→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Villa Section
const VillaSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.3);

  const villaFeatures = [
    { number: "50+", label: "Curated Villas" },
    { number: "1-10", label: "Bedrooms" },
    { number: "€2K-50K", label: "Per Night" }
  ];

  return (
    <section className="villa-section" id="villas" ref={ref}>
      <div className="villa-image">
        <img
          src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/stbarthinsider/villa-wakeup.jpg"
          alt="Luxury villa"
        />
      </div>
      <div className={`villa-content ${isVisible ? 'animate-in' : ''}`}>
        <span className="section-label light">Private Retreats</span>
        <h2 className="villa-title">Your <em>Villa</em> Awaits</h2>
        <p className="villa-text">
          From hillside hideaways with panoramic ocean views to beachfront estates with private coves —
          we match you with the perfect villa for your St Barth story.
        </p>
        <div className="villa-features-grid">
          {villaFeatures.map((feature, index) => (
            <div key={index} className="villa-feature">
              <span className="villa-feature-number">{feature.number}</span>
              <span className="villa-feature-label">{feature.label}</span>
            </div>
          ))}
        </div>
        <div className="villa-amenities">
          <div className="amenity">
            <span className="amenity-icon">◈</span>
            <span>Infinity Pools</span>
          </div>
          <div className="amenity">
            <span className="amenity-icon">◈</span>
            <span>Private Chef</span>
          </div>
          <div className="amenity">
            <span className="amenity-icon">◈</span>
            <span>Daily Housekeeping</span>
          </div>
          <div className="amenity">
            <span className="amenity-icon">◈</span>
            <span>Concierge Service</span>
          </div>
        </div>
        <a href="#contact" className="villa-cta">
          Find Your Villa
          <span className="cta-arrow">→</span>
        </a>
      </div>
    </section>
  );
};

// Yacht Section
const YachtSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);

  return (
    <section className="yacht-section" id="yachts" ref={ref}>
      <div className={`yacht-text ${isVisible ? 'animate-in' : ''}`}>
        <span className="section-label light">Charter Experience</span>
        <h2>Yacht <em>Adventures</em></h2>
        <p className="yacht-intro">Discover St Barth from the sea aboard our luxury vessels</p>
        <a href="#contact" className="yacht-cta">
          Book Your Charter
          <span className="cta-arrow">→</span>
        </a>
      </div>
      <div className={`yacht-cards ${isVisible ? 'animate-in' : ''}`}>
        <div className="yacht-option">
          <span className="yacht-duration">2 hours</span>
          <h4>Sunset Cruise</h4>
          <p>Golden hour over Gustavia with champagne</p>
          <span className="yacht-price">from €1,500</span>
        </div>
        <div className="yacht-option yacht-popular">
          <span className="yacht-popular-tag">most popular</span>
          <span className="yacht-duration">4 hours</span>
          <h4>Half Day</h4>
          <p>Explore hidden coves and pristine beaches</p>
          <span className="yacht-price">from €3,000</span>
        </div>
        <div className="yacht-option">
          <span className="yacht-duration">8 hours</span>
          <h4>Full Day</h4>
          <p>Island hopping with gourmet lunch</p>
          <span className="yacht-price">from €5,500</span>
        </div>
      </div>
    </section>
  );
};

// Regatta 2026 Section
const RegattaSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.15);

  return (
    <section className="regatta-section" id="regatta" ref={ref}>
      <div className={`regatta-content ${isVisible ? 'animate-in' : ''}`}>
        <div className="regatta-top">
          <div className="regatta-header">
            <div className="regatta-event-tag">
              <span className="pulse-dot"></span>
              limited availability
            </div>
            <h2>
              St Barths Bucket 2026
              <span>The world's most prestigious superyacht regatta — and the island's most exclusive week.</span>
            </h2>
          </div>
          <div className="regatta-date">
            <span className="regatta-date-badge">{"// save the date"}</span>
            <span className="regatta-date-main">March 12 — 15</span>
          </div>
        </div>

        <div className="regatta-grid">
          <div className="regatta-card">
            <div className="regatta-card-img">
              <img src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/stbarthinsider/regatta-villa-package.jpg" alt="Regatta Villa Package" />
            </div>
            <div className="regatta-card-body">
              <div className="regatta-card-icon">01</div>
              <h4>Regatta Villa Packages</h4>
              <p>Premium waterfront villas with direct harbor views. Watch the race from your infinity pool. Includes private chef and daily housekeeping.</p>
              <span className="regatta-card-tag">from €8,500 / week</span>
            </div>
          </div>
          <div className="regatta-card">
            <div className="regatta-card-img">
              <img src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/stbarthinsider/regatta.jpg" alt="Race Day Yacht Viewing" />
            </div>
            <div className="regatta-card-body">
              <div className="regatta-card-icon">02</div>
              <h4>Race Day Yacht Viewing</h4>
              <p>Front-row seats from the water. Board our luxury catamaran with champagne, gourmet lunch, and the best vantage point on the island.</p>
              <span className="regatta-card-tag">full day experience</span>
            </div>
          </div>
          <div className="regatta-card">
            <div className="regatta-card-img">
              <img src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/stbarthinsider/concierge.jpeg" alt="Full Concierge Package" />
            </div>
            <div className="regatta-card-body">
              <div className="regatta-card-icon">03</div>
              <h4>Full Concierge Package</h4>
              <p>Villa + yacht + private transfers + restaurant reservations + event access. One price, zero stress. Your entire regatta week, handled.</p>
              <span className="regatta-card-tag">all-inclusive</span>
            </div>
          </div>
        </div>

        <div className="regatta-bottom">
          <p className="regatta-urgency">Bucket week books out <strong>months in advance</strong> — secure your spot now.</p>
          <a href="https://wa.me/590691283484?text=Hi! I'm interested in the St Barths Bucket 2026 packages." className="regatta-cta" target="_blank" rel="noopener noreferrer">
            Secure Your Spot
            <span className="cta-arrow">→</span>
          </a>
        </div>
      </div>
    </section>
  );
};

// Services Cards Section
const ServiceCard = ({ title, subtitle, image, index }) => {
  const [isVisible, setIsVisible] = useState(false);
  const cardRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setTimeout(() => setIsVisible(true), index * 150);
        }
      },
      { threshold: 0.2 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div ref={cardRef} className={`service-card ${isVisible ? 'visible' : ''}`}>
      <div className="service-card-image">
        <img src={image} alt={title} />
        <div className="service-card-overlay"></div>
      </div>
      <div className="service-card-content">
        <span className="service-card-subtitle">{subtitle}</span>
        <h3 className="service-card-title">{title}</h3>
      </div>
    </div>
  );
};

const ServicesSection = () => {
  const services = [
    {
      title: 'Private Villas',
      subtitle: 'Accommodation',
      image: 'https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/accomodation.jpg'
    },
    {
      title: 'Yacht Charters',
      subtitle: 'Maritime',
      image: 'https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/yacht-jump.jpg'
    },
    {
      title: 'Fine Dining',
      subtitle: 'Gastronomy',
      image: 'https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/nikki-beach.jpg'
    },
    {
      title: 'Private Aviation',
      subtitle: 'Transport',
      image: 'https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/st-barth-plane.jpeg'
    }
  ];

  return (
    <section className="services-section" id="services">
      <div className="services-header">
        <span className="section-label">Our Services</span>
        <h2>Curated <em>Experiences</em></h2>
      </div>
      <div className="services-grid">
        {services.map((service, index) => (
          <ServiceCard key={index} {...service} index={index} />
        ))}
      </div>
    </section>
  );
};

// Animated Counter Component
// Contact Section
const ContactSection = () => {
  const [ref, isVisible] = useScrollAnimation(0.2);
  const [formState, setFormState] = useState({ firstName: '', lastName: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const res = await fetch('https://formspree.io/f/mreaznae', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          firstName: formState.firstName,
          lastName: formState.lastName,
          email: formState.email,
          message: formState.message,
        }),
      });
      if (res.ok) {
        setFormState({ firstName: '', lastName: '', email: '', message: '' });
        alert('Thank you! We will contact you shortly.');
      } else {
        alert('Something went wrong. Please try again.');
      }
    } catch {
      alert('Something went wrong. Please try again.');
    }
    setIsSubmitting(false);
  };

  return (
    <section className="contact-section" id="contact" ref={ref}>
      <div className="contact-image">
        <img
          src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/upper-view-sbh.jpg"
          alt="St Barth aerial view"
        />
      </div>
      <div className={`contact-content ${isVisible ? 'animate-in' : ''}`}>
        <span className="section-label">get in touch</span>
        <h2>Request Your <em>Proposal</em></h2>
        <p>You'll speak directly with our head concierge, not a call center. Average response time: 1 hour.</p>
        <form className="contact-form" onSubmit={handleSubmit}>
          <div className="form-row">
            <input
              type="text"
              name="firstName"
              placeholder="First Name"
              value={formState.firstName}
              onChange={(e) => setFormState({...formState, firstName: e.target.value})}
              required
            />
            <input
              type="text"
              name="lastName"
              placeholder="Last Name"
              value={formState.lastName}
              onChange={(e) => setFormState({...formState, lastName: e.target.value})}
              required
            />
          </div>
          <input
            type="email"
            name="email"
            placeholder="Email Address"
            value={formState.email}
            onChange={(e) => setFormState({...formState, email: e.target.value})}
            required
          />
          <textarea
            name="message"
            placeholder="Tell us about your dream experience..."
            value={formState.message}
            onChange={(e) => setFormState({...formState, message: e.target.value})}
            required
          ></textarea>
          <button type="submit" className={`btn-submit ${isSubmitting ? 'loading' : ''}`} disabled={isSubmitting}>
            {isSubmitting ? 'Sending...' : 'Request My Proposal'}
          </button>
        </form>
        <div className="contact-alternatives">
          <p>Or reach us directly</p>
          <div className="contact-buttons">
            <a href="https://wa.me/590691283484" className="contact-btn whatsapp" target="_blank" rel="noopener noreferrer">
              <svg viewBox="0 0 24 24" fill="currentColor" width="20" height="20">
                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
              </svg>
              WhatsApp
            </a>
            <a href="tel:+590691283484" className="contact-btn phone">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" width="20" height="20">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72 12.84 12.84 0 00.7 2.81 2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45 12.84 12.84 0 002.81.7A2 2 0 0122 16.92z"/>
              </svg>
              Call Us
            </a>
          </div>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  return (
    <footer className="footer">
      <div className="footer-content">
        <div className="footer-brand">
          <img src="https://lllqncjwqcbrygqujdin.supabase.co/storage/v1/object/public/media/st-barth-inside-logo.png" alt="St Barth Insider" className="footer-logo" />
          <p>Your exclusive gateway to St Barthélemy's finest experiences.</p>
        </div>
        <div className="footer-links">
          <div className="footer-column">
            <h4>Services</h4>
            <a href="#villas">Private Villas</a>
            <a href="#yachts">Yacht Charters</a>
            <a href="#dining">Fine Dining</a>
          </div>
          <div className="footer-column">
            <h4>Contact</h4>
            <a href="mailto:hello@stbarthinsider.com">hello@stbarthinsider.com</a>
            <a href="tel:+590691283484">+590 691 283 484</a>
            <p>Gustavia, St Barthélemy</p>
          </div>
        </div>
      </div>
      <div className="footer-bottom">
        <p>© 2026 The St Barth Insider. All rights reserved.</p>
      </div>
    </footer>
  );
};

// Main App Component
function App() {
  return (
    <div className="App">
      <ScrollProgress />
      <Navigation />
      <Hero />
      <RegattaSection />
      <AboutSection />
      <ServicesSection />
      <ExperiencesSection />
      <VillaSection />
      <YachtSection />
      <TrustSection />
      <ContactSection />
      <Footer />
      <WhatsAppButton />
    </div>
  );
}

export default App;
